<template>
    <v-card :elevation="1" class="background-2" width="100%">
        <v-card-text v-if="isLoaded">
            <v-row class="mx-0">
                <!-- Pannello sinistro: Colonne configurate -->
                <v-col cols="6">
                    <v-card outlined class="pa-2" style="max-height: 700px; overflow: auto; padding-bottom: 60% !important;">
                        <v-card-title class="text-subtitle-1">
                            Colonne Configurate
                        </v-card-title>
                        <v-divider class="mb-3"></v-divider>

                        <draggable
                            v-model="selectedFields"
                            :group="{ 
                                name: 'fields',
                                pull: true, 
                                put: function(to, from) {
                                    return from.options.group.name === 'fields';
                                }
                            }"
                            handle=".drag-handle"
                            class="pa-2"
                            ghostClass="ghost-element"
                            chosenClass="chosen-element"
                            :swapThreshold="0.50"
                            @change="updateFieldOrder">

                            <v-card v-for="field in selectedFields"
                                :key="field.id"
                                outlined
                                class="field-card mb-2">
                                <v-row class="ma-0 px-2 py-1">
                                    <v-col cols="10" class="pa-2 d-flex align-center">
                                        <v-icon class="pr-2 drag-handle">mdi-drag-vertical-variant</v-icon>
                                        <span class="drag-handle">{{ field.label }} {{ field.customfield ? '(CP)' : '' }}</span>
                                    </v-col>

                                    <v-col cols="1" class="pa-2">
                                        <v-tooltip bottom>
                                            <template v-slot:activator="{ on, attrs }">
                                                <v-btn 
                                                    icon 
                                                    small 
                                                    @click="removeField(field)" 
                                                    color="error"
                                                    v-bind="attrs"
                                                    v-on="on">
                                                    <v-icon>mdi-delete</v-icon>
                                                </v-btn>
                                            </template>
                                            <span>Rimuovi Colonna</span>
                                        </v-tooltip>
                                    </v-col>

                                    <v-col cols="1" class="pa-2">
                                        <v-btn icon small @click="toggleExpand(field)" color="primary">
                                            <v-icon>{{ isExpanded(field) ? 'mdi-chevron-up' : 'mdi-chevron-down' }}</v-icon>
                                        </v-btn>
                                    </v-col>
                                </v-row>

                                <div v-show="isExpanded(field)" class="expand-content">
                                    <v-divider></v-divider>
                                    <v-row class="ma-0 pa-2">
                                        <v-col cols="12" class="pa-2">
                                            <v-switch
                                                v-if="isSummable(field)"
                                                v-model="field.total"
                                                label="Calcola totale in fondo alla colonna"
                                                inset
                                                hide-details
                                                class="mb-2 ma-0"
                                            ></v-switch>
                                            <v-switch
                                                v-model="field.permissions.show_exclude"
                                                @change="applyRolePermissions(field)"
                                                :label="'Escludi questi ruoli dalla visualizzazione di questo campo: ' + (field.permissions.show_exclude ? 'Si' : 'No')"
                                                inset
                                                hide-details
                                                class="mb-2 ma-0"
                                            ></v-switch>
                                            <v-select
                                                v-model="field.permissions.show"
                                                @change="applyRolePermissions(field)"
                                                :items="roles"
                                                :label="rolePermissionLabel('show', field.permissions)"
                                                multiple
                                                dense
                                                chips
                                                small-chips
                                                :menu-props="{'offset-y': true}"
                                                outlined
                                                hide-details
                                            ></v-select>
                                        </v-col>
                                    </v-row>
                                </div>
                            </v-card>
                        </draggable>

                        <v-card-actions class="pt-3">
                            <v-row no-gutters>
                                <v-col cols="6" class="pr-1">
                                    <v-btn 
                                        block
                                        color="primary"
                                        @click="addAllFields"
                                        :disabled="!availableFields.length">
                                        <v-icon left>mdi-arrow-all</v-icon>
                                        Aggiungi tutte
                                    </v-btn>
                                </v-col>
                                <v-col cols="6" class="pl-1">
                                    <v-btn 
                                        block
                                        color="error"
                                        @click="clearAllFields"
                                        :disabled="!selectedFields.length">
                                        <v-icon left>mdi-delete-sweep</v-icon>
                                        Svuota tutto
                                    </v-btn>
                                </v-col>
                            </v-row>
                        </v-card-actions>
                    </v-card>
                </v-col>

                <!-- Pannello destro: Colonne disponibili -->
                <v-col cols="6">
                    <v-card outlined class="pa-2" style="max-height: 700px; overflow: auto; padding-bottom: 60% !important;">
                        <v-card-title class="text-subtitle-1">
                            Colonne Disponibili
                            <v-spacer></v-spacer>
                            <v-text-field
                                v-model="searchField"
                                append-icon="mdi-magnify"
                                label="Cerca"
                                hide-details
                                dense
                                outlined
                                clearable
                                style="max-width: 240px"
                            ></v-text-field>
                        </v-card-title>
                        <v-divider class="mb-3"></v-divider>
                        
                        <draggable
                            :list="availableFields"
                            :group="{ 
                                name: 'fields', 
                                pull: 'clone', 
                                put: false,
                                revertClone: true
                            }"
                            :sort="false"
                            :clone="cloneField">
                            <template v-for="field in availableFields">
                                <v-card 
                                    v-if="matchesSearch(field)"
                                    :key="field.id"
                                    class="cursor-pointer mb-1"
                                    outlined>
                                    <v-card-text class="py-2">
                                        <v-icon class="pr-2 drag-handle">mdi-drag-vertical-variant</v-icon>
                                        {{ field.label }} {{ field.customfield ? '(CP)' : '' }}
                                    </v-card-text>
                                </v-card>
                            </template>
                        </draggable>
                    </v-card>
                </v-col>
            </v-row>
        </v-card-text>

        <v-divider></v-divider>
        
        <v-card-actions class="pa-3">
            <v-spacer></v-spacer>
            <v-btn color="primary" @click="saveConfiguration">
                Salva Configurazione
            </v-btn>
        </v-card-actions>
    </v-card>
</template>

<script>
import draggable from 'vuedraggable'
import FieldRolePermissionsMixin from '@/mixins/FieldRolePermissionsMixin'

export default {
    name: 'FieldsTableSelector',
    components: {
        draggable
    },

    mixins: [FieldRolePermissionsMixin],

    props: {
        modelDef: {
            type: String,
            required: true
        },
        templateData: {
            type: String,
            default: 'template_data'
        },
        info: {
            type: String,
            default: 'info'
        },
        mobile: {
            type: Boolean,
            default: false
        }
    },

    data() {
        return {
            isLoaded: false,
            roles: [],
            selectedFields: [],
            allFields: [],
            expandedFields: {},
            searchField: '',
        }
    },

    computed: {
        endpointUrl() {
            return this.mobile ? 'settings/fields-mobile-model/' : 'settings/fields-model/'
        },
        availableFields: {
            get() {
                const usedFieldIds = new Set(this.selectedFields.map(f => f.id))
                return this.allFields.filter(field => !usedFieldIds.has(field.id))
            },
            set(value) {
                // Necessario per il v-model ma non fa nulla
            }
        }
    },

    methods: {
        async loadConfiguration() {
            try {
                const [fieldsResponse, rolesResponse] = await Promise.all([
                    this.axios.post(this.endpointUrl, {
                        model: this.modelDef,
                        template_data: this.templateData,
                        info: this.info
                    }),
                    this.axios.post('getoptionfields/', {model: 'Role', relation_field: 'name', key: 'text', value: 'value'})
                ])

                this.roles = this.roleItemsWithAll(rolesResponse.data.option_fields)
                this.allFields = fieldsResponse.data.template_table_fields.fieldDefs.map(field => ({
                    id: field.key,
                    label: this.$trans(field.label),
                    type: field.type || null,
                    customfield: field.customfield || false,
                    total: false,
                    permissions: {
                        show: ['*'],
                        show_exclude: false
                    }
                }))

                // Carica configurazione esistente se presente
                const fieldSettings = fieldsResponse.data.fields_table_configuration
                if (fieldSettings && fieldSettings.columns) {
                    this.selectedFields = Object.entries(fieldSettings.columns)
                        .map(([fieldId, fieldConfig]) => {
                            const originalField = this.allFields.find(f => f.id === fieldId)
                            return {
                                id: fieldId,
                                label: originalField?.label || fieldId,
                                type: originalField?.type || null,
                                customfield: originalField?.customfield || false,
                                // Un flag rimasto in config su un campo non piu'
                                // sommabile (es. tipo cambiato) non va riproposto
                                total: Boolean(fieldConfig.total) && this.isSummable({ type: originalField?.type }),
                                permissions: this.normalizeRolePermissions(fieldConfig.permissions)
                            }
                        })
                }

                this.isLoaded = true
            } catch (error) {
                console.error('Error loading configuration:', error)
            }
        },

        cloneField(field) {
            return {
                ...field,
                total: false,
                permissions: {
                    show: ['*'],
                    show_exclude: false
                }
            }
        },

        // Il totale di colonna ha senso solo sui tipi sommabili: stessa
        // whitelist di summableColumnTypes() lato backend
        isSummable(field) {
            return ['currency', 'number', 'duration'].includes(field.type)
        },

        removeField(field) {
            const index = this.selectedFields.indexOf(field)
            if (index > -1) {
                this.selectedFields.splice(index, 1)
            }
        },

        toggleExpand(field) {
            this.$set(this.expandedFields, field.id, !this.expandedFields[field.id])
        },

        isExpanded(field) {
            return Boolean(this.expandedFields[field.id])
        },

        updateFieldOrder() {
            // Aggiorna ordine se necessario
        },

        matchesSearch(field) {
            if (!this.searchField) return true
            const searchTerm = this.searchField.toLowerCase()
            return field.label.toLowerCase().includes(searchTerm)
        },

        async saveConfiguration() {
            const configuration = {
                columns: this.selectedFields.reduce((acc, field, index) => {
                    acc[field.id] = {
                        order: index + 1,
                        permissions: field.permissions
                    }
                    if(field.customfield) {
                        acc[field.id].customfield = true
                    }
                    if(field.total && this.isSummable(field)) {
                        acc[field.id].total = true
                    }
                    return acc
                }, {})
            }

            try {
                // Chiave dinamica basata su info (la tabella usa $info, non $template_data) e mobile
                const mobileInfix = this.mobile ? '_mobile' : ''
                const configKey = this.info !== 'info'
                    ? `fields_table${mobileInfix}_configuration_${this.info}`
                    : `fields_table${mobileInfix}_configuration`

                await this.axios.post('settings/', {
                    model: this.modelDef,
                    [configKey]: configuration
                })

                this.$store.commit('setSnackbar', {
                    timeout: 6000,
                    text: 'Configurazione colonne salvata con successo',
                    value: true,
                    color: 'success'
                })

            } catch (error) {
                console.error('Error saving configuration:', error)
            }
        },

        addAllFields() {
            const newFields = this.availableFields.map(field => this.cloneField(field))
            this.selectedFields.push(...newFields)
        },

        clearAllFields() {
            this.selectedFields = []
        }
    },

    mounted() {
        this.loadConfiguration()
    }
}
</script>

<style scoped>
.field-card {
    transition: all 0.3s ease;
}

.expand-content {
    transition: all 0.3s ease;
    overflow: hidden;
}

.ghost-element {
    background: #BBDEFB !important; 
    opacity: 0.5;
}

.chosen-element {
    background: #BBDEFB !important; 
    opacity: 0.5;
}    

.drag-handle {
    cursor: move;
}

.cursor-pointer {
    cursor: pointer;
}
</style>
